// Tiroir panier : ouverture/fermeture (<dialog> modal), re-rendu Liquid via la Section Rendering API
// après chaque changement du panier, actions quantité / retrait / options / upsell via window.MpPanier.
if (!customElements.get('mp-tiroir-panier')) {
  class MpTiroirPanier extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-mp-tiroir-dialog]');
      this.rafraichissement = null;

      // Le lien panier du header ouvre le tiroir
      this.surClicPanier = (event) => {
        const lien = event.target.closest('[data-mp-panier-lien]');
        if (!lien || event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        this.ouvrir(lien);
      };
      document.addEventListener('click', this.surClicPanier);

      this.surMaj = () => this.rafraichir();
      this.surAjout = async () => {
        // Nouvel ajout : la réservation repartira de zéro à l'ouverture du tiroir
        try {
          sessionStorage.removeItem('mp-reservation-fin');
        } catch {}
        await this.rafraichir();
        this.ouvrir();
      };
      document.addEventListener('mp:panier:maj', this.surMaj);
      document.addEventListener('mp:panier:ajoute', this.surAjout);

      this.dialog.addEventListener('click', (event) => this.surClic(event));
      this.dialog.addEventListener('change', (event) => this.surChangement(event));
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.fermer();
        }
      });
      this.dialog.addEventListener('close', () => this.surFermeture());
      this.nbNormalisations = 0;
      this.normaliserOffert();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.surClicPanier);
      document.removeEventListener('mp:panier:maj', this.surMaj);
      document.removeEventListener('mp:panier:ajoute', this.surAjout);
    }

    ouvrir(declencheur) {
      this.declencheur = declencheur || document.activeElement;
      if (this.dialog.open) return;
      this.querySelector('mp-tiroir-reservation')?.demarrer();
      document.documentElement.classList.add('mp-tiroir-ouvert');
      this.dialog.showModal();
      requestAnimationFrame(() => this.dialog.classList.add('is-ouvert'));
    }

    fermer() {
      this.dialog.classList.remove('is-ouvert');
      const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => this.dialog.close(), reduit ? 0 : 250);
    }

    surFermeture() {
      document.documentElement.classList.remove('mp-tiroir-ouvert');
      this.declencheur?.focus?.();
    }

    surClic(event) {
      // Clic sur le fond (hors panneau)
      if (event.target === this.dialog) {
        this.fermer();
        return;
      }
      if (event.target.closest('[data-mp-tiroir-fermer]')) {
        this.fermer();
        return;
      }

      const quantite = event.target.closest('[data-mp-quantite-ligne]');
      if (quantite) {
        this.executer(() => window.MpPanier.changer(quantite.dataset.mpQuantiteLigne, Number(quantite.dataset.quantite)));
        return;
      }

      const retirer = event.target.closest('[data-mp-retirer]');
      if (retirer) {
        this.executer(() => window.MpPanier.changer(retirer.dataset.mpRetirer, 0));
        return;
      }

      const upsell = event.target.closest('[data-mp-upsell]');
      if (upsell) {
        this.executer(() => window.MpPanier.ajouter([{ id: Number(upsell.dataset.mpUpsell), quantity: 1 }]));
      }
    }

    surChangement(event) {
      const choixOffert = event.target.closest('[data-mp-offert-option]');
      if (choixOffert) {
        this.changerOffert(choixOffert.closest('[data-mp-offert]'));
        return;
      }

      const option = event.target.closest('[data-mp-tiroir-option]');
      if (!option) return;
      if (option.checked) {
        this.executer(() => window.MpPanier.ajouter([{ id: Number(option.value), quantity: 1 }]));
      } else {
        this.executer(() => window.MpPanier.retirerVariante(option.value));
      }
    }

    // Produit offert : remplace la ligne par la variante choisie (taille + couleur), même quantité,
    // toujours marquée « _offert » pour rester dans la carte cadeau
    changerOffert(carte) {
      if (!carte) return;
      let variantes = [];
      try {
        variantes = JSON.parse(carte.querySelector('[data-mp-offert-variantes]')?.textContent || '[]');
      } catch {}
      const choix = [...carte.querySelectorAll('[data-mp-offert-option]:checked')]
        .sort((a, b) => Number(a.dataset.mpOffertOption) - Number(b.dataset.mpOffertOption))
        .map((input) => input.value);
      const variante = variantes.find((v) => v.options.every((valeur, i) => valeur === choix[i]));
      if (!variante) return;

      const cle = carte.dataset.mpOffert;
      this.executer(async () => {
        const panier = await window.MpPanier.obtenir();
        const ligne = panier.items.find((article) => article.key === cle);
        if (!ligne || ligne.variant_id === variante.id) return;
        await fetch(`${window.mpConfig?.racine || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: cle, quantity: 0 }),
        });
        await window.MpPanier.ajouter([{ id: variante.id, quantity: ligne.quantity, properties: { _offert: '1' } }]);
      });
    }

    // Toute action passe par MpPanier, qui émet « mp:panier:maj » : le tiroir se re-rend alors
    async executer(action) {
      if (!window.MpPanier) return;
      const contenu = this.querySelector('[data-mp-tiroir-contenu]');
      contenu?.setAttribute('aria-busy', 'true');
      try {
        await action();
      } catch (erreur) {
        this.annoncer(erreur.message);
        await this.rafraichir();
      } finally {
        this.querySelector('[data-mp-tiroir-contenu]')?.removeAttribute('aria-busy');
      }
    }

    // Plusieurs événements rapprochés = un seul rendu
    rafraichir() {
      if (this.rafraichissement) return this.rafraichissement;
      this.rafraichissement = this.chargerSection().finally(() => {
        this.rafraichissement = null;
      });
      return this.rafraichissement;
    }

    async chargerSection() {
      const racine = window.mpConfig?.racine || '/';
      const id = this.dataset.section;
      const reponse = await fetch(`${racine}?sections=${encodeURIComponent(id)}`);
      if (!reponse.ok) return;
      const sections = await reponse.json();
      if (!sections[id]) return;

      const html = new DOMParser().parseFromString(sections[id], 'text/html');
      const nouveau = html.querySelector('[data-mp-tiroir-contenu]');
      const actuel = this.querySelector('[data-mp-tiroir-contenu]');
      if (!nouveau || !actuel) return;

      // Conserver le focus sur l'action équivalente après le re-rendu
      const focusAvant = document.activeElement;
      const cleFocus = focusAvant && actuel.contains(focusAvant) ? this.cleDeFocus(focusAvant) : null;

      actuel.replaceWith(nouveau);
      // Boutons de paiement express (Apple Pay…) : à réinitialiser après chaque re-rendu
      window.Shopify?.PaymentButton?.init?.();
      this.annoncer(nouveau.querySelector('#mp-tiroir-titre')?.textContent.trim().replace(/\s+/g, ' '));

      if (cleFocus) {
        (nouveau.querySelector(cleFocus) || nouveau.querySelector('[data-mp-tiroir-fermer]'))?.focus();
      }
      this.normaliserOffert();
    }

    // Unité offerte par la remise mais pas encore sur une ligne « _offert » (repérée par Liquid) :
    // elle y est déplacée, même variante, pour afficher la carte « produit offert » avec son choix
    // taille / couleur. Montant inchangé. Limité à quelques passages pour ne jamais boucler.
    normaliserOffert() {
      const contenu = this.querySelector('[data-mp-offert-normaliser]');
      if (!contenu || this.normalisationEnCours || this.nbNormalisations >= 3 || !window.MpPanier) return;
      this.normalisationEnCours = true;
      this.nbNormalisations += 1;
      const { mpOffertNormaliser: cle, variante, unites, reste } = contenu.dataset;
      this.executer(async () => {
        await fetch(`${window.mpConfig?.racine || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: cle, quantity: Number(reste) }),
        });
        await window.MpPanier.ajouter([{ id: Number(variante), quantity: Number(unites), properties: { _offert: '1' } }]);
      }).finally(() => {
        this.normalisationEnCours = false;
      });
    }

    cleDeFocus(element) {
      for (const attribut of ['data-mp-quantite-ligne', 'data-mp-retirer', 'data-mp-upsell']) {
        if (element.hasAttribute(attribut)) {
          const suffixe = attribut === 'data-mp-quantite-ligne' ? `[data-sens="${element.dataset.sens}"]` : '';
          return `[${attribut}="${element.getAttribute(attribut)}"]${suffixe}`;
        }
      }
      return element.id ? `#${CSS.escape(element.id)}` : null;
    }

    annoncer(message) {
      const zone = this.querySelector('[data-mp-tiroir-annonce]');
      if (zone && message) zone.textContent = message;
    }
  }

  customElements.define('mp-tiroir-panier', MpTiroirPanier);
}

// Bandeau confiance rotatif du tiroir (mêmes transitions que la barre promo).
// Custom element : se réinitialise tout seul à chaque re-rendu du tiroir.
if (!customElements.get('mp-tiroir-confiance')) {
  class MpTiroirConfiance extends HTMLElement {
    connectedCallback() {
      this.messages = [...this.querySelectorAll('.mp-tiroir__bandeau-message')];
      if (this.messages.length < 2) return;
      this.index = 0;
      const vitesse = Number(this.dataset.vitesse) || 3400;
      this.minuteur = setInterval(() => {
        const sortant = this.messages[this.index];
        sortant.classList.remove('is-active');
        sortant.classList.add('is-sortant');
        sortant.setAttribute('aria-hidden', 'true');
        setTimeout(() => sortant.classList.remove('is-sortant'), 700);
        this.index = (this.index + 1) % this.messages.length;
        this.messages[this.index].classList.add('is-active');
        this.messages[this.index].removeAttribute('aria-hidden');
      }, vitesse);
    }

    disconnectedCallback() {
      clearInterval(this.minuteur);
    }
  }

  customElements.define('mp-tiroir-confiance', MpTiroirConfiance);
}

// Bandeau « commande réservée » : compte à rebours purement visuel (réglage de la
// section, allégation trompeuse en vente réelle). Il démarre à la durée complète (5 min)
// la première fois qu'on ouvre le tiroir après un ajout au panier ; l'échéance vit en
// sessionStorage : elle survit aux re-rendus et aux réouvertures, et repart à l'expiration.
if (!customElements.get('mp-tiroir-reservation')) {
  class MpTiroirReservation extends HTMLElement {
    connectedCallback() {
      this.affichage = this.querySelector('[data-mp-reservation-temps]');
      this.duree = (Number(this.dataset.minutes) || 5) * 60000;
      this.minuteur = setInterval(() => this.tic(), 1000);
      if (this.closest('dialog')?.open) this.demarrer();
      this.tic();
    }

    disconnectedCallback() {
      clearInterval(this.minuteur);
    }

    lireFin() {
      try {
        return Number(sessionStorage.getItem('mp-reservation-fin')) || 0;
      } catch {
        return 0;
      }
    }

    // Tiroir ouvert : lance le compte à rebours s'il n'est pas déjà en cours
    demarrer() {
      const fin = this.lireFin();
      if (fin - Date.now() > 0 && fin - Date.now() <= this.duree) return;
      try {
        sessionStorage.setItem('mp-reservation-fin', String(Date.now() + this.duree));
      } catch {}
      this.tic();
    }

    tic() {
      const fin = this.lireFin();
      let reste = this.duree / 1000;
      if (fin) {
        reste = Math.round((fin - Date.now()) / 1000);
        if (reste <= 0) {
          // Échéance passée : nouvelle réservation complète
          try {
            sessionStorage.setItem('mp-reservation-fin', String(Date.now() + this.duree));
          } catch {}
          reste = this.duree / 1000;
        }
      }
      const minutes = String(Math.floor(reste / 60)).padStart(2, '0');
      const secondes = String(reste % 60).padStart(2, '0');
      this.affichage.textContent = `${minutes}:${secondes}`;
    }
  }

  customElements.define('mp-tiroir-reservation', MpTiroirReservation);
}
